import { Box, Button, Container, Stack } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { holdTypeService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { Header, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router'
import { HoldTypeBasicInfoCard } from './organisms/HoldTypeBasicInfoCard'
import { HoldTypeRulesCard } from './organisms/HoldTypeRulesCard'
import { HoldTypeFormStore } from './stores/HoldTypeFormStore'

import { InitializationWrapper } from '@planning/components'
import { invalidateHoldTypesCache } from '@planning/utils/hold-enforcement'
import { RoleOption } from './HoldTypesPage'

interface Props {
  userRoleOptions?: RoleOption[]
}

export const AddHoldTypePage = observer(({ userRoleOptions = [] }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const theme = useMinimalsTheme()
  const { appViewStore } = usePlanningStore()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id

  const formStore = useMemo(() => new HoldTypeFormStore(), [])

  const { loading: isInitializing } = useAsyncFetch(async () => {
    try {
      const allTypes = await holdTypeService.get()
      formStore.setExistingHoldTypeNames(allTypes.map(t => t.name))

      if (isEditMode) {
        const data = await holdTypeService.getById(Number(id))
        if (data) {
          formStore.setData({
            type: data.name,
            description: data.description ?? '',
            userRoles: data.userRoles || [],
            isActive: data.isActive,
            restrictions: data.restrictions || [],
          })
          formStore.setOriginalName(data.name)
        }
      }
    } catch (error) {
      appViewStore.setShowAlert('error', t('failedToLoadData', 'Failed to load data'))
      navigate(PATHS.masterData.holdTypes.url)
    }
  }, [id, isEditMode, formStore])

  const title = isEditMode ? t('editHoldType', 'Edit hold type') : t('addHoldType', 'Add hold type')

  const breadcrumbs = [
    { label: t(PATHS.masterData.title, 'Master data'), route: PATHS.masterData.url },
    { label: t('holdTypes', 'Hold Types'), route: PATHS.masterData.holdTypes.url },
    { label: title },
  ]

  const handleCancel = () => {
    navigate(PATHS.masterData.holdTypes.url)
  }

  const handleSave = async () => {
    const isValid = formStore.validate()
    if (!isValid) return

    try {
      await formStore.submit(isEditMode ? Number(id) : undefined)
      invalidateHoldTypesCache()
      appViewStore.setShowAlert(
        'success',
        isEditMode
          ? t('holdTypeUpdatedSuccessfully', 'Hold type updated successfully')
          : t('holdTypeCreatedSuccessfully', 'Hold type created successfully'),
      )
      navigate(PATHS.masterData.holdTypes.url)
    } catch {
      appViewStore.setShowAlert('error', t('failedToSaveHoldType', 'Failed to save hold type'))
    }
  }

  return (
    <Box sx={{ height: '100%', bgcolor: theme.palette.grey[100] }}>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Header
        title={title}
        breadcrumbs={breadcrumbs}
        rightRenderOption={
          <Stack direction='row' spacing={theme.customSpacing.xs}>
            <Button
              variant='text'
              color='secondary'
              onClick={handleCancel}
              data-cy='cancel-hold-type-btn'
            >
              {t('cancel', 'Cancel')}
            </Button>
            <Button variant='contained' onClick={handleSave} data-cy='save-hold-type-btn'>
              {t('save', 'Save')}
            </Button>
          </Stack>
        }
      />
      <InitializationWrapper isInitializing={isInitializing}>
        <Container maxWidth='md' sx={{ paddingY: theme.customSpacing.m }}>
          <Stack spacing={theme.customSpacing.m}>
            <HoldTypeBasicInfoCard formStore={formStore} userRoleOptions={userRoleOptions} />
            <HoldTypeRulesCard formStore={formStore} />
          </Stack>
        </Container>
      </InitializationWrapper>
    </Box>
  )
})
